/**
 * What you can do to one message (owner's prototype, 2026-08-22).
 *
 * Two things, and deliberately only two: copy the text, and quote it back.
 * The prototype's menu had a third — delete — and it is not here, because
 * nothing on this side can delete a message. A knock transcript is the record
 * of an assessment somebody paid for and a doorman thread is the owner's own
 * standing instructions; neither has a delete route, and a menu item that
 * cannot do its job is worse than no menu item at all.
 *
 * The menu is `position: fixed` at the pointer, because it is answering a
 * gesture on a bubble rather than opening off a button. Escape closes it, a
 * pointer anywhere outside closes it, and either way focus goes back where the
 * conversation is.
 */
import { useEffect, useRef, type CSSProperties } from "react";
import { trimQuote, type Quote } from "../../lib/quote";

export const COPY_LABEL = "Copy text";
export const QUOTE_LABEL = "Quote";
export const MESSAGE_MENU_LABEL = "Message actions";
export const CANCEL_QUOTE_LABEL = "Cancel quote";
export const COPIED_COPY = "Copied.";

/** How long a press has to last on a touch screen before it counts (prototype). */
export const LONG_PRESS_MS = 420;

const FOCUS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70 " +
  "focus-visible:ring-inset";

function CopyGlyph() {
  return (
    <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0" fill="none" aria-hidden="true">
      <path
        d="M7.5 7.5V5.25c0-.69.56-1.25 1.25-1.25h6c.69 0 1.25.56 1.25 1.25v6c0 .69-.56 1.25-1.25 1.25H12.5M5.25 7.5h6c.69 0 1.25.56 1.25 1.25v6c0 .69-.56 1.25-1.25 1.25h-6C4.56 16 4 15.44 4 14.75v-6c0-.69.56-1.25 1.25-1.25Z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function QuoteGlyph() {
  return (
    <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0" fill="none" aria-hidden="true">
      <path
        d="M8.5 5.5 4 10l4.5 4.5M4 10h6.5a5 5 0 0 1 5 5v.5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CrossGlyph() {
  return (
    <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="none" aria-hidden="true">
      <path d="M6 6l8 8M14 6l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export type MenuTarget = { x: number; y: number; quote: Quote };

/**
 * The floating menu. It is placed from the pointer and then pulled back inside
 * the viewport, because a menu opened on the last bubble of a thread would
 * otherwise open under the composer or off the bottom of a phone.
 */
export function BubbleMenu({
  target,
  onCopy,
  onQuote,
  onClose,
}: {
  target: MenuTarget;
  onCopy: () => void;
  onQuote: () => void;
  onClose: () => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const first = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Focus the first item: a menu that opens with focus left on the bubble is
    // a menu a keyboard cannot reach, and Escape below has nothing to return.
    first.current?.focus();
    const key = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      // The shell's own Escape cascade must not also fire: one press shuts one
      // thing, and the thing on top is this.
      event.stopPropagation();
      onClose();
    };
    const away = (event: PointerEvent) => {
      if (!box.current?.contains(event.target as Node)) onClose();
    };
    window.addEventListener("keydown", key, true);
    window.addEventListener("pointerdown", away);
    return () => {
      window.removeEventListener("keydown", key, true);
      window.removeEventListener("pointerdown", away);
    };
  }, [onClose]);

  const width = 168;
  const height = 92;
  const viewportW = typeof window === "undefined" ? 360 : window.innerWidth;
  const viewportH = typeof window === "undefined" ? 640 : window.innerHeight;
  const style: CSSProperties = {
    left: Math.min(Math.max(8, target.x - width / 2), Math.max(8, viewportW - width - 8)),
    top: Math.min(Math.max(8, target.y - height - 8), Math.max(8, viewportH - height - 8)),
    width,
  };

  return (
    <div
      ref={box}
      role="menu"
      aria-label={MESSAGE_MENU_LABEL}
      data-testid="bubble-menu"
      className="nob-msg-menu"
      style={style}
    >
      <button ref={first} type="button" role="menuitem" onClick={onCopy} className={FOCUS}>
        <CopyGlyph />
        {COPY_LABEL}
      </button>
      <button type="button" role="menuitem" onClick={onQuote} className={FOCUS}>
        <QuoteGlyph />
        {QUOTE_LABEL}
      </button>
    </div>
  );
}

/**
 * The quote standing above the composer, waiting to be sent with whatever is
 * typed next. Dismissible, because changing your mind about what you are
 * answering must not mean clearing the field you already typed into.
 */
export function QuoteBar({ quote, onClear }: { quote: Quote; onClear: () => void }) {
  return (
    <div className="nob-quote-bar mb-2" data-testid="quote-bar">
      <span className="min-w-0 flex-1">
        {quote.speaker && (
          <span className="block text-[11px] font-semibold text-text-soft">{quote.speaker}</span>
        )}
        <span className="block truncate text-xs text-muted">{trimQuote(quote.text)}</span>
      </span>
      <button
        type="button"
        aria-label={CANCEL_QUOTE_LABEL}
        onClick={onClear}
        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-faint
                    transition-colors duration-75 hover:text-text-soft ${FOCUS}`}
      >
        <CrossGlyph />
      </button>
    </div>
  );
}

/**
 * The quote as it appears INSIDE a landed bubble — the block the prototype
 * draws above the message text, tinted from the bubble's own colour so it
 * works in the doorman's zinc and the sender's amber without either being
 * named here.
 */
export function QuotedPrefix({ quote }: { quote: Quote }) {
  return (
    <span className="nob-quote-in" data-testid="quoted-prefix">
      {quote.speaker && (
        <span className="block text-[10px] font-semibold opacity-70">{quote.speaker}</span>
      )}
      {quote.text}
    </span>
  );
}

/**
 * The gesture that opens the menu, as one set of handlers a bubble spreads.
 *
 * Three ways in, because there are three kinds of pointer: a right-click on a
 * desktop, a long press on a touch screen, and — the one the prototype had no
 * need for and this does — a keyboard. A bubble carrying a menu is focusable
 * and answers the context-menu key, so the whole feature is reachable without
 * a pointer at all.
 *
 * A long press that turns into a scroll is not a long press: any movement past
 * a few pixels cancels the timer, or a thread cannot be scrolled with a thumb.
 */
export function useBubbleMenu(open: (target: MenuTarget) => void, quote: Quote) {
  const timer = useRef(0);
  const from = useRef({ x: 0, y: 0 });

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const cancel = () => window.clearTimeout(timer.current);

  return {
    tabIndex: 0,
    role: "button" as const,
    "aria-haspopup": "menu" as const,
    "aria-label": MESSAGE_MENU_LABEL,
    onContextMenu: (event: React.MouseEvent) => {
      event.preventDefault();
      open({ x: event.clientX, y: event.clientY, quote });
    },
    onPointerDown: (event: React.PointerEvent) => {
      // A mouse has a context menu of its own; only touch and pen wait.
      if (event.pointerType === "mouse") return;
      from.current = { x: event.clientX, y: event.clientY };
      const { clientX, clientY } = event;
      cancel();
      timer.current = window.setTimeout(() => {
        open({ x: clientX, y: clientY, quote });
      }, LONG_PRESS_MS);
    },
    onPointerMove: (event: React.PointerEvent) => {
      const dx = Math.abs(event.clientX - from.current.x);
      const dy = Math.abs(event.clientY - from.current.y);
      if (dx > 8 || dy > 8) cancel();
    },
    onPointerUp: cancel,
    onPointerCancel: cancel,
    onKeyDown: (event: React.KeyboardEvent) => {
      // The dedicated key, and the Shift+F10 every screen reader teaches.
      const menuKey = event.key === "ContextMenu" || (event.shiftKey && event.key === "F10");
      if (!menuKey) return;
      event.preventDefault();
      const box = (event.currentTarget as HTMLElement).getBoundingClientRect();
      open({ x: box.left + box.width / 2, y: box.top, quote });
    },
  };
}
